import type { Observation } from '@medplum/fhirtypes'
import { differenceInDays, isSameDay, subMonths } from 'date-fns'
import { orderBy } from 'lodash-es'
import { hasCodeableConcept } from '#src/codeableConcept.ts'
import { formatClinicalUnit } from '#src/display.ts'
import { BODY_WEIGHT_CODING } from '#src/models/loinc.ts'

/*
 * Weight change calculation utilities for clinical nutrition.
 *
 * Formula: % Weight Change = ((UBW - Current) / UBW) x 100
 * UBW (Usual Body Weight) = first recorded weight observation.
 * All subsequent observations are compared against UBW.
 *
 * Significant-loss thresholds follow the Academy/A.S.P.E.N. consensus criteria.
 */

interface WeightChangeResult {
  absoluteChangeLbs: number
  percentChange: number
  direction: 'loss' | 'gain' | 'stable'
  isSignificantLoss: boolean
}

interface WeightChangeFromUBW {
  ubwLbs: number
  ubwDate: string
  currentLbs: number
  currentDate: string
  elapsedMonths: number
  result: WeightChangeResult
}

type WeightChangeFromUBWResult =
  | { ok: true; data: WeightChangeFromUBW }
  | { ok: false; reason: 'is-ubw' | 'insufficient-data' | 'missing-fields' }

/*
 * Academy/A.S.P.E.N. weight-loss thresholds per time period, chronic-illness
 * column. A loss at or above the threshold is significant. There is no
 * severity grade here on purpose: A.S.P.E.N. grades severity from 2 of 6
 * clinical characteristics, never from weight percentage alone.
 */
const SIGNIFICANT_LOSS_THRESHOLDS = {
  1: 5,
  3: 7.5,
  6: 10,
  12: 20,
} as const

function computeWeightChange(
  ubwLbs: number,
  currentLbs: number
): WeightChangeResult {
  const absoluteChangeLbs = roundToOneDecimal(currentLbs - ubwLbs)

  if (absoluteChangeLbs === 0) {
    return {
      absoluteChangeLbs: 0,
      percentChange: 0,
      direction: 'stable',
      isSignificantLoss: false,
    }
  }

  const percentChange =
    ubwLbs === 0 ? 0 : Math.abs(((ubwLbs - currentLbs) / ubwLbs) * 100)
  const direction: WeightChangeResult['direction'] =
    absoluteChangeLbs < 0 ? 'loss' : 'gain'

  return {
    absoluteChangeLbs,
    percentChange: roundToOneDecimal(percentChange),
    direction,
    isSignificantLoss: false,
  }
}

function assessSignificance(
  result: WeightChangeResult,
  periodMonths: number
): WeightChangeResult {
  if (result.direction !== 'loss') {
    return { ...result, isSignificantLoss: false }
  }

  // percentChange is already rounded to one decimal, so the flag agrees with
  // the percentage the panel displays: a raw 19.96% loss reads as 20.0% and is
  // flagged at the 12-month threshold.
  return {
    ...result,
    isSignificantLoss:
      result.percentChange >= getThresholdForPeriod(periodMonths),
  }
}

function getThresholdForPeriod(periodMonths: number): number {
  const buckets = [12, 6, 3, 1] as const
  for (const bucket of buckets) {
    if (periodMonths >= bucket) {
      return SIGNIFICANT_LOSS_THRESHOLDS[bucket]
    }
  }
  return SIGNIFICANT_LOSS_THRESHOLDS[1]
}

function computeWeightChangeFromUBW(
  observations: Observation[],
  selectedObservation?: Observation
): WeightChangeFromUBWResult {
  if (observations.length < 2) {
    return { ok: false, reason: 'insufficient-data' }
  }

  const sorted = orderBy(
    observations,
    (observation) => getObservationDate(observation) ?? '',
    ['asc']
  )

  const target = selectedObservation ?? sorted[sorted.length - 1]
  const targetWeight = getObservationWeight(target)

  // When multiple observations share the earliest calendar date, pick the one
  // furthest from the target weight — this maximizes percent change (and
  // whether it is flagged) regardless of loss vs gain.
  const earliestDate = new Date(getObservationDate(sorted[0]) ?? '')
  const ubw = sorted
    .filter((obs) => {
      const d = getObservationDate(obs)
      return d != null && isSameDay(new Date(d), earliestDate)
    })
    .reduce((best, obs) => {
      const bestWeight = getObservationWeight(best)
      const obsWeight = getObservationWeight(obs)
      if (bestWeight == null) {
        return obs
      }
      if (obsWeight == null || targetWeight == null) {
        return best
      }
      return Math.abs(obsWeight - targetWeight) >
        Math.abs(bestWeight - targetWeight)
        ? obs
        : best
    })

  const ubwWeight = getObservationWeight(ubw)
  const ubwDate = getObservationDate(ubw)

  if (ubw.id === target.id) {
    if (ubwWeight == null || !ubwDate) {
      return { ok: false, reason: 'missing-fields' }
    }
    return { ok: false, reason: 'is-ubw' }
  }

  const targetDate = getObservationDate(target)

  if (ubwWeight == null || !ubwDate || targetWeight == null || !targetDate) {
    return { ok: false, reason: 'missing-fields' }
  }

  const daysBetween = Math.abs(
    differenceInDays(new Date(targetDate), new Date(ubwDate))
  )
  const elapsedMonths = Math.max(1, Math.round(daysBetween / 30))

  const result = assessSignificance(
    computeWeightChange(ubwWeight, targetWeight),
    elapsedMonths
  )

  return {
    ok: true,
    data: {
      ubwLbs: ubwWeight,
      ubwDate,
      currentLbs: targetWeight,
      currentDate: targetDate,
      elapsedMonths,
      result,
    },
  }
}

const LOOKBACK_BUCKETS = [1, 3, 6, 12] as const
const BUCKET_TOLERANCE = 0.5

interface BucketRow {
  months: number
  label: string
  result: WeightChangeResult | null
  lookbackLbs: number | null
  lookbackDate: string | null
  currentLbs: number | null
}

function computeBucketRows(
  observations: Observation[],
  selectedObservation: Observation
): BucketRow[] {
  const selectedDate = getObservationDate(selectedObservation)
  const currentLbs = getObservationWeight(selectedObservation)

  if (!selectedDate || currentLbs == null) {
    return LOOKBACK_BUCKETS.map((months) => ({
      months,
      label: formatElapsedPeriod(months),
      result: null,
      lookbackLbs: null,
      lookbackDate: null,
      currentLbs: null,
    }))
  }

  const claimedIds = new Set<string>()

  return LOOKBACK_BUCKETS.map((months) => {
    const targetDate = subMonths(new Date(selectedDate), months)
    const label = formatElapsedPeriod(months)

    let nearest: Observation | null = null
    let nearestLbs: number | null = null
    let nearestDiff = Infinity

    for (const obs of observations) {
      if (obs.id === selectedObservation.id) {
        continue
      }
      if (obs.id && claimedIds.has(obs.id)) {
        continue
      }
      const obsDate = getObservationDate(obs)
      const obsWeight = getObservationWeight(obs)
      if (
        !obsDate ||
        obsWeight == null ||
        new Date(obsDate) > new Date(selectedDate)
      ) {
        continue
      }
      const elapsedDays = differenceInDays(
        new Date(selectedDate),
        new Date(obsDate)
      )
      const windowDays = months * 30 * BUCKET_TOLERANCE
      if (elapsedDays < windowDays) {
        continue
      }
      const diffDays = Math.abs(differenceInDays(new Date(obsDate), targetDate))
      if (diffDays <= windowDays) {
        const isCloser = diffDays < nearestDiff
        // Same date: prefer the weight furthest from current — maximizes
        // percent change (and whether it is flagged) regardless of loss vs gain.
        const isSameDayFurtherFromCurrent =
          diffDays === nearestDiff &&
          Math.abs(obsWeight - currentLbs) >
            Math.abs((nearestLbs ?? currentLbs) - currentLbs)
        if (isCloser || isSameDayFurtherFromCurrent) {
          nearestDiff = diffDays
          nearest = obs
          nearestLbs = obsWeight
        }
      }
    }

    if (!nearest || nearestLbs == null) {
      return {
        months,
        label,
        result: null,
        lookbackLbs: null,
        lookbackDate: null,
        currentLbs: null,
      }
    }

    if (nearest.id) {
      claimedIds.add(nearest.id)
    }

    const lookbackLbs = nearestLbs
    const rawResult = computeWeightChange(lookbackLbs, currentLbs)
    const result = assessSignificance(rawResult, months)

    return {
      months,
      label,
      result,
      lookbackLbs,
      lookbackDate: getObservationDate(nearest),
      currentLbs,
    }
  })
}

function formatWeightChangeDisplay(
  result: WeightChangeResult,
  unit = 'lbs'
): string {
  if (result.direction === 'stable') {
    return `0 ${unit} / stable`
  }

  const sign = result.absoluteChangeLbs > 0 ? '+' : ''

  return `${sign}${result.absoluteChangeLbs} ${unit} / ${result.percentChange.toFixed(1)}% ${result.direction}`
}

function isBodyWeightObservation(observation: Observation): boolean {
  return hasCodeableConcept(observation.code, BODY_WEIGHT_CODING)
}

const displayWeightUnit = formatClinicalUnit

function formatElapsedPeriod(months: number): string {
  return `${months} month${months === 1 ? '' : 's'}`
}

function getObservationDate(observation: Observation): string | null {
  return observation.effectiveDateTime ?? observation.issued ?? null
}

function getObservationWeight(observation: Observation): number | null {
  return observation.valueQuantity?.value ?? null
}

function roundToOneDecimal(value: number): number {
  return Math.round(value * 10) / 10
}

export {
  computeWeightChange,
  assessSignificance,
  computeWeightChangeFromUBW,
  computeBucketRows,
  formatWeightChangeDisplay,
  formatElapsedPeriod,
  displayWeightUnit,
  isBodyWeightObservation,
}

export type {
  WeightChangeResult,
  WeightChangeFromUBW,
  WeightChangeFromUBWResult,
  BucketRow,
}
